import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, Download, AlertTriangle, CheckCircle2, FileArchive,
  ShieldAlert, GitMerge, Loader2, ChevronDown, ChevronUp, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { Combobox } from '../components/Combobox';
import { FilterField } from '../components/FilterField';
import ImportDiffModal from '../components/ImportDiffModal';
import { useAbbinatoreFacets } from '../hooks/useAbbinatoreFacets';
import {
  fetchImportExportStatus, exportCatalog, importCatalog, previewImportCatalog, downloadImportTemplate,
  ImportExportStatus, ImportSummary, ImportDiff,
} from '../api';

function downloadBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

export default function ImportExportSection() {
  const [mainTab, setMainTab] = useState<'importa' | 'esporta'>('importa');

  return (
    <div className="max-w-[1180px] mx-auto" data-testid="import-export-section">
      <div className="mb-6">
        <h1 className="text-[24px] font-semibold text-[#1f1f1d] mb-1">Import / Export</h1>
        <p className="text-[15px] text-[#6d6d68]">
          Catalogo macchine, relazioni rebrand e abbinamenti con i codici DTK degli articoli.
        </p>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-[#e2e2df]" data-testid="ie-main-tabs">
        {(['importa', 'esporta'] as const).map(t => (
          <button
            key={t}
            onClick={() => setMainTab(t)}
            data-testid={`ie-main-tab-${t}`}
            className={`px-4 py-2.5 text-[15px] font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              mainTab === t ? 'border-[#2b3a8f] text-[#2b3a8f]' : 'border-transparent text-[#6d6d68] hover:text-[#3a3a36]'
            }`}
          >
            {t === 'importa' ? <Upload className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            {t === 'importa' ? 'Importa' : 'Esporta'}
          </button>
        ))}
      </div>

      {mainTab === 'importa' ? <ImportPanel /> : <ExportPanel />}
    </div>
  );
}

// ── IMPORTA ──────────────────────────────────────────────────────────

function ImportPanel() {
  const [status, setStatus] = useState<ImportExportStatus | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [file, setFile] = useState<File | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [diff, setDiff] = useState<ImportDiff | null>(null);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchImportExportStatus().then(setStatus).catch(() => {}); }, []);

  const needsConfirm = mode === 'replace' && !!status && (status.machines > 0 || status.associations > 0);
  const confirmOk = !needsConfirm || confirmText.trim().toLowerCase() === 'conferma';
  const canSubmit = !!file && confirmOk && !previewing;

  const handleModeChange = (m: 'merge' | 'replace') => {
    setMode(m);
    setConfirmText('');
    setResult(null);
    setError(null);
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const { blob, filename } = await downloadImportTemplate();
      downloadBlob(blob, filename);
    } catch {
      toast.error('Errore durante il download del template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!file || !confirmOk) return;
    setPreviewing(true);
    setError(null);
    setResult(null);
    try {
      const d = await previewImportCatalog(file, mode);
      setDiff(d);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Errore durante l'analisi del file");
      toast.error(detail || "Errore durante l'analisi del file");
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!file || !confirmOk) return;
    setSubmitting(true);
    setError(null);
    try {
      const summary = await importCatalog(file, mode, mode === 'replace' ? confirmText.trim() : undefined);
      setResult(summary);
      toast.success(mode === 'replace' ? 'Catalogo sostituito con successo' : 'Catalogo unito con successo');
      setDiff(null);
      setFile(null);
      setConfirmText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchImportExportStatus().then(setStatus).catch(() => {});
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Errore durante l'importazione");
      toast.error(detail || "Errore durante l'importazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[760px]" data-testid="import-panel">
      {/* Info formato */}
      <div className="bg-[#f7f8fc] border border-[#dfe3f5] rounded-[11px] px-4 py-3.5 mb-4 text-[14px] text-[#3a3a36]">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="font-semibold flex items-center gap-1.5"><FileArchive className="w-3.5 h-3.5" /> Formato file richiesto</div>
          <button
            onClick={handleDownloadTemplate}
            disabled={templateLoading}
            data-testid="ie-download-template-btn"
            className="flex items-center gap-1.5 text-[13px] font-semibold text-[#2b3a8f] hover:underline disabled:opacity-50 flex-none"
          >
            {templateLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            Scarica template di esempio
          </button>
        </div>
        <p className="mb-1.5">
          Uno ZIP con due CSV (separatore <code className="bg-white px-1 rounded border border-[#d8d8d4]">;</code>):
        </p>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-[#5c5c57]">
          <li><code className="font-mono">macchine.csv</code> — Marca, Modello, TipologiaCodice, RebrandDiMarca, RebrandDiModello, CategorieEscluseRebrand</li>
          <li><code className="font-mono">abbinamenti.csv</code> — Marca, Modello, TipologiaCodice, CodiceDTK</li>
        </ul>
        <p className="mt-1.5 text-[13px] text-[#5c5c57]">
          Le macchine sono identificate da Marca+Modello (non da ID interni).
        </p>
        <p className="mt-1.5 text-[13px] text-[#5c5c57]">
          <strong>Marca</strong>: scrivi il nome esattamente come già presente nel catalogo (es. XEROX, KYOCERA MITA, HP) —
          verificalo nel filtro Marca della tab Esporta. <strong>Categorie escluse</strong>: elenca i codici categoria
          separati da virgola, senza spazi (es. <code className="font-mono">toner,opc-drum</code>) — l'elenco completo dei
          codici validi è nel filtro Categoria della tab Esporta → Filtra per Articoli.
        </p>
      </div>

      {/* Selettore modalità */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button
          onClick={() => handleModeChange('merge')}
          data-testid="ie-mode-merge"
          className={`text-left px-4 py-3.5 rounded-[11px] border-2 transition-colors ${
            mode === 'merge' ? 'border-[#2b3a8f] bg-[#eef1fb]' : 'border-[#e2e2df] bg-white hover:bg-[#fafafa]'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold text-[15px] text-[#1f1f1d] mb-1">
            <GitMerge className="w-4 h-4 text-[#2b3a8f]" /> Unisci
          </div>
          <p className="text-[13px] text-[#5c5c57]">
            Aggiunge macchine e abbinamenti mancanti. Non rimuove né sovrascrive nulla di esistente.
          </p>
        </button>
        <button
          onClick={() => handleModeChange('replace')}
          data-testid="ie-mode-replace"
          className={`text-left px-4 py-3.5 rounded-[11px] border-2 transition-colors ${
            mode === 'replace' ? 'border-[#8a3b3b] bg-[#fdf5f5]' : 'border-[#e2e2df] bg-white hover:bg-[#fafafa]'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold text-[15px] text-[#1f1f1d] mb-1">
            <ShieldAlert className="w-4 h-4 text-[#8a3b3b]" /> Sostituisci tutto
          </div>
          <p className="text-[13px] text-[#5c5c57]">
            Cancella l'intero catalogo attuale e lo ricrea dal file. Operazione distruttiva e irreversibile.
          </p>
        </button>
      </div>

      {/* Warning + conferma per replace */}
      {needsConfirm && (
        <div className="bg-[#fdf5f5] border border-[#f3d5d5] rounded-[11px] px-4 py-3.5 mb-4" data-testid="ie-replace-warning">
          <div className="flex items-center gap-2 font-semibold text-[14px] text-[#8a3b3b] mb-1.5">
            <AlertTriangle className="w-4 h-4" /> Attenzione: operazione irreversibile
          </div>
          <p className="text-[13px] text-[#6d4747] mb-2.5">
            Il catalogo attuale contiene <strong>{status?.machines.toLocaleString('it')} macchine</strong> e{' '}
            <strong>{status?.associations.toLocaleString('it')} abbinamenti</strong>. Verranno cancellati interamente e
            sostituiti dal contenuto del file. Scrivi <code className="bg-white px-1 rounded border border-[#f3d5d5]">conferma</code> per procedere.
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="Scrivi qui: conferma"
            data-testid="ie-confirm-input"
            className="w-full max-w-[280px] px-3 py-2 border border-[#e3b8b8] rounded-[8px] text-[14px] bg-white focus:outline-none focus:border-[#8a3b3b]"
          />
        </div>
      )}

      {/* File input + submit */}
      <div className="flex items-center gap-3 mb-2">
        <label
          htmlFor="ie-file-input"
          data-testid="ie-file-label"
          className="px-4 py-2.5 rounded-[9px] border border-[#cfcfca] bg-white text-[14px] font-semibold text-[#3a3a36] hover:bg-[#fafafa] cursor-pointer transition-colors flex items-center gap-2"
        >
          <FileArchive className="w-3.5 h-3.5" /> Scegli file .zip
        </label>
        <input
          ref={fileInputRef}
          id="ie-file-input"
          type="file"
          accept=".zip"
          onChange={e => setFile(e.target.files?.[0] ?? null)}
          data-testid="ie-file-input"
          className="hidden"
        />
        <span className="text-[14px] text-[#5c5c57] truncate max-w-[300px]">
          {file ? file.name : 'Nessun file selezionato'}
        </span>
      </div>

      <button
        onClick={handlePreview}
        disabled={!canSubmit}
        data-testid="ie-import-submit"
        className={`px-4 py-2.5 rounded-[9px] text-[15px] font-semibold flex items-center gap-2 transition-colors ${
          mode === 'replace' ? 'bg-[#8a3b3b] hover:bg-[#712f2f]' : 'bg-[#2b3a8f] hover:bg-[#243279]'
        } text-white disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {previewing ? 'Analisi del file in corso…' : mode === 'replace' ? 'Sostituisci catalogo' : 'Importa e unisci'}
      </button>

      {diff && (
        <ImportDiffModal
          diff={diff}
          onClose={() => setDiff(null)}
          onConfirm={handleConfirmImport}
          confirming={submitting}
        />
      )}

      {error && (
        <div className="mt-3 text-[14px] text-[#8a3b3b] bg-[#fdf5f5] border border-[#f3d5d5] rounded-[9px] px-3.5 py-2.5" data-testid="ie-import-error">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4 bg-white border border-[#e2e2df] rounded-[11px] overflow-hidden" data-testid="ie-import-result">
          <div className="px-4 py-3 border-b border-[#eee] flex items-center gap-2 font-semibold text-[15px] text-[#1f1f1d]">
            <CheckCircle2 className="w-4 h-4 text-[#2f8a4f]" /> Importazione completata ({result.mode === 'replace' ? 'sostituzione' : 'unione'})
          </div>
          <div className="grid grid-cols-3 gap-3 px-4 py-3.5">
            <Stat label="Macchine create" value={result.machines_created} />
            <Stat label="Macchine già esistenti" value={result.machines_matched} />
            <Stat label="Rebrand impostati" value={result.rebrand_links_set} />
            <Stat label="Rebrand non modificati" value={result.rebrand_links_skipped} sub="già avevano un padre" />
            <Stat label="Abbinamenti creati" value={result.associations_created} />
            <Stat label="Abbinamenti già presenti" value={result.associations_skipped_existing} />
          </div>
          {result.errors_total > 0 && (
            <div className="border-t border-[#eee] px-4 py-3">
              <button
                onClick={() => setErrorsOpen(o => !o)}
                data-testid="ie-import-errors-toggle"
                className="flex items-center gap-1.5 text-[13px] font-semibold text-[#8a3b3b]"
              >
                {errorsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {result.errors_total} righe ignorate (avvisi)
              </button>
              {errorsOpen && (
                <ul className="mt-2 space-y-1 text-[12.5px] font-mono text-[#5c5c57] max-h-[220px] overflow-y-auto">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  {result.errors_total > result.errors.length && (
                    <li className="italic">…e altre {result.errors_total - result.errors.length}</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="bg-[#f7f7f5] rounded-[9px] px-3 py-2.5">
      <div className="text-[20px] font-semibold text-[#1f1f1d] leading-none">{value.toLocaleString('it')}</div>
      <div className="text-[12px] text-[#5c5c57] mt-1">{label}</div>
      {sub && <div className="text-[11px] text-[#8a8a85] mt-0.5">{sub}</div>}
    </div>
  );
}

// ── ESPORTA ──────────────────────────────────────────────────────────

function ExportPanel() {
  const [scope, setScope] = useState<'machines' | 'articles'>('machines');

  // Filtri Macchine
  const [mBrand, setMBrand] = useState<string | null>(null);
  const [mTipo, setMTipo] = useState<string | null>(null);
  const [mSearch, setMSearch] = useState('');
  const [mUnmatched, setMUnmatched] = useState(false);

  // Filtri Articoli
  const [aBrand, setABrand] = useState<string | null>(null);
  const [aCategory, setACategory] = useState<string | null>(null);
  const [aSearch, setASearch] = useState('');
  const [aUnmatched, setAUnmatched] = useState(false);

  const [exporting, setExporting] = useState<string | null>(null);

  // Due cascate indipendenti: i due pannelli hanno marche diverse (macchine
  // vs catalogo) e filtri successivi diversi. Prima condividevano un'unica
  // lista di marche macchina, anche nel pannello articoli.
  const macchineFacets = useAbbinatoreFacets('machines', mBrand, {
    value: mTipo, onChange: setMTipo,
  });
  const articoliFacets = useAbbinatoreFacets('articles', aBrand, {
    value: aCategory, onChange: setACategory,
  });

  const mBrandOpts = macchineFacets.marche;
  const tipologie = macchineFacets.tipologie;
  const aBrandOpts = articoliFacets.marche;
  const catOpts = articoliFacets.categorie;

  const handleExport = async (which: 'full' | 'machines' | 'articles') => {
    setExporting(which);
    try {
      const f = which === 'machines'
        ? { scope: 'machines' as const, search: mSearch || undefined, brand: mBrand || undefined, tipo: mTipo || undefined, unmatched: mUnmatched }
        : which === 'articles'
        ? { scope: 'articles' as const, search: aSearch || undefined, brand: aBrand || undefined, category: aCategory || undefined, unmatched: aUnmatched }
        : { scope: 'full' as const };
      const { blob, filename } = await exportCatalog(f);
      downloadBlob(blob, filename);
      toast.success('Export avviato');
    } catch {
      toast.error("Errore durante l'export");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div data-testid="export-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-1 bg-[#f3f3f0] rounded-[9px] p-1" data-testid="ie-export-scope-tabs">
          {(['machines', 'articles'] as const).map(s => (
            <button
              key={s}
              onClick={() => setScope(s)}
              data-testid={`ie-export-scope-${s}`}
              className={`px-3.5 py-1.5 rounded-[7px] text-[14px] font-semibold transition-colors ${
                scope === s ? 'bg-white text-[#2b3a8f] shadow-sm' : 'text-[#6d6d68]'
              }`}
            >
              {s === 'machines' ? 'Filtra per Macchine' : 'Filtra per Articoli'}
            </button>
          ))}
        </div>

        <button
          onClick={() => handleExport('full')}
          disabled={exporting !== null}
          data-testid="ie-export-full-btn"
          className="px-4 py-2 rounded-[9px] border border-[#cfcfca] bg-white text-[14px] font-semibold text-[#3a3a36] hover:bg-[#fafafa] flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          {exporting === 'full' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Esporta catalogo completo
        </button>
      </div>

      {scope === 'machines' ? (
        <div className="bg-white border border-[#e2e2df] rounded-[11px] px-4 py-4" data-testid="ie-export-machines-filters">
          <div className="flex items-center gap-4 flex-wrap mb-4">
            <FilterField label="Marca" className="w-[280px] flex-none">
              <Combobox value={mBrand} onChange={setMBrand} options={mBrandOpts} placeholder="Tutte le marche" testId="ie-m-filter-brand" emptyText="Nessuna marca" />
            </FilterField>
            <FilterField label="Tipologia" className="w-[200px] flex-none">
              <select
                value={mTipo ?? ''}
                onChange={e => setMTipo(e.target.value || null)}
                data-testid="ie-m-filter-tipo"
                className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] bg-white focus:outline-none focus:border-[#2b3a8f]"
              >
                <option value="">Tutte</option>
                {tipologie.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
              </select>
            </FilterField>
            <FilterField label="Cerca modello" className="w-[190px] flex-none">
              <input
                type="text"
                value={mSearch}
                onChange={e => setMSearch(e.target.value)}
                placeholder="Codice o nome modello…"
                data-testid="ie-m-filter-search"
                className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] focus:outline-none focus:border-[#2b3a8f]"
              />
            </FilterField>
            <label className="flex items-center gap-2 text-[14px] text-[#5c5c57] cursor-pointer flex-none">
              <input type="checkbox" checked={mUnmatched} onChange={e => setMUnmatched(e.target.checked)} data-testid="ie-m-filter-unmatched" />
              Solo senza associazioni
            </label>
          </div>
          <button
            onClick={() => handleExport('machines')}
            disabled={exporting !== null}
            data-testid="ie-export-machines-btn"
            className="px-4 py-2.5 rounded-[9px] bg-[#2b3a8f] text-white text-[14px] font-semibold hover:bg-[#243279] flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {exporting === 'machines' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Esporta selezione
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#e2e2df] rounded-[11px] px-4 py-4" data-testid="ie-export-articles-filters">
          <div className="flex items-center gap-4 flex-wrap mb-4">
            <FilterField label="Marca" className="w-[280px] flex-none">
              <Combobox value={aBrand} onChange={setABrand} options={aBrandOpts} placeholder="Tutte le marche" testId="ie-a-filter-brand" emptyText="Nessuna marca" />
            </FilterField>
            <FilterField label="Categoria" className="w-[220px] flex-none">
              <Combobox value={aCategory} onChange={setACategory} options={catOpts} placeholder="Tutte le categorie" testId="ie-a-filter-category" emptyText="Nessuna categoria" />
            </FilterField>
            <FilterField label="Cerca" className="w-[190px] flex-none">
              <input
                type="text"
                value={aSearch}
                onChange={e => setASearch(e.target.value)}
                placeholder="SKU, titolo…"
                data-testid="ie-a-filter-search"
                className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] focus:outline-none focus:border-[#2b3a8f]"
              />
            </FilterField>
            <label className="flex items-center gap-2 text-[14px] text-[#5c5c57] cursor-pointer flex-none">
              <input type="checkbox" checked={aUnmatched} onChange={e => setAUnmatched(e.target.checked)} data-testid="ie-a-filter-unmatched" />
              Solo senza compatibilità
            </label>
          </div>
          <button
            onClick={() => handleExport('articles')}
            disabled={exporting !== null}
            data-testid="ie-export-articles-btn"
            className="px-4 py-2.5 rounded-[9px] bg-[#2b3a8f] text-white text-[14px] font-semibold hover:bg-[#243279] flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {exporting === 'articles' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Esporta selezione
          </button>
        </div>
      )}
    </div>
  );
}
